import * as Yup from 'yup';
import { endOfDay, subYears } from 'date-fns';
import { GENDER, LANGUAGES, MIN_USER_AGE } from '../../constants';
import {
  armCellPhoneNumberPrefixes,
  getSocCardValidation,
} from '../userFormValidations';
import { APPLICANT_TYPES } from '../../constants/claim';
import { EMAIL_VALIDATION_PATTERN, ONLY_DIGITS_REGEX_ALT } from '../../constants/patterns';
import { getPassportsValidation } from '../../helpers/armenianPassportsValidationHelper';
import { isArmenianCountry } from '../../helpers/countryHelper';

export const getPhysicalEntityValidator = ({
  citizenshipKey = 'applicantCitizenship',
  birthdayKey = 'applicantBDay',
  firstNameKey = 'applicantFName',
  lastNameKey = 'applicantLName',
  passportKey = 'applicantPassNum',
  passportGivenDateKey = 'applicantPassGivenDate',
  passportValidUntilKey = 'applicantPassValidUntilDate',
  passportFromKey = 'applicantPassFrom',
  genderKey,
  socCardKey = 'applicantSocCard',
  when = 'applicantIs',
  is = (applicantIs) => applicantIs === APPLICANT_TYPES[1].value,
} = {}) => {
  const validationSchema = {
    [citizenshipKey]: Yup.object()
      .nullable()
      .when(when, {
        is,
        then: Yup.object().required('validationErrors.isRequired'),
      }),
    [birthdayKey]: Yup.date()
      .nullable()
      .when(when, {
        is,
        then: Yup.date()
          .max(subYears(new Date(), MIN_USER_AGE), 'validationErrors.minAge')
          .required('validationErrors.isRequired'),
      }),
    [firstNameKey]: Yup.string()
      .nullable()
      .when(when, {
        is,
        then: Yup.string()
          .required('validationErrors.isRequired')
          .isCorrectLanguage(),
      }),
    [lastNameKey]: Yup.string()
      .nullable()
      .when(when, {
        is,
        then: Yup.string()
          .required('validationErrors.isRequired')
          .isCorrectLanguage(),
      }),
    [passportKey]: Yup.string()
      .nullable()
      .when(when, {
        is,
        then: Yup.string()
          .when(citizenshipKey, (
            citizenship, schema, test,
          ) => getPassportsValidation(citizenship, schema, test)),
      }),
    [socCardKey]: Yup.string()
      .nullable()
      .when(when, {
        is,
        then: Yup.string()
          .when(citizenshipKey, (citizenship, schema) => getSocCardValidation(citizenship, schema)),
      }),
    [passportGivenDateKey]: Yup.date()
      .nullable()
      .when(when, {
        is,
        then: Yup.date().when(citizenshipKey, (citizenship, schema) => {
          if (citizenship && !isArmenianCountry(citizenship)) {
            return schema
              .test(
                passportGivenDateKey,
                'validationErrors.validityDate',
                (value) => new Date(value) <= endOfDay(new Date()),
              )
              .required('validationErrors.isRequired');
          }
          return schema;
        }),
      }),
    [passportValidUntilKey]: Yup.date()
      .nullable()
      .when(when, {
        is,
        then: Yup.date().when(citizenshipKey, (citizenship, schema) => {
          if (citizenship && !isArmenianCountry(citizenship)) {
            return schema
              .test(
                passportValidUntilKey,
                'validationErrors.validityDate',
                (value) => endOfDay(new Date(value)) >= new Date(),
              )
              .required('validationErrors.isRequired');
          }
          return schema;
        }),
      }),
    [passportFromKey]: Yup.string().when(when, {
      is,
      then: Yup.string().when(citizenshipKey, (citizenship, schema) => {
        if (citizenship && !isArmenianCountry(citizenship)) {
          return schema.required('validationErrors.isRequired');
        }
        return schema;
      }),
    }),
  };

  if (genderKey) {
    validationSchema[genderKey] = Yup.number()
      .nullable()
      .when(when, {
        is,
        then: Yup.number()
          .oneOf([GENDER.MALE, GENDER.FEMALE], 'validationErrors.isRequired')
          .required('validationErrors.isRequired')
          .nullable(),
      });
  }

  return validationSchema;
};

export const getLegalEntityValidator = ({
  hvhh = 'applicantOrganizationHvhh',
  organizationName = 'applicantOrganizationName',
  email = 'applicantOrganizationEmail',
  phone = 'applicantOrganizationPhone',
  when = 'applicantIs',
  is = (applicantIs) => applicantIs === APPLICANT_TYPES[2].value,
} = {}) => ({

  [hvhh]: Yup.string()
    .nullable()
    .when(when, {
      is,
      then: Yup.string()
        .matches(/^[\d]{8}$/, 'validationErrors.eightDigitsOnly')
        .required('validationErrors.isRequired'),
    }),
  [`${organizationName}HY`]: Yup.string()
    .nullable()
    .when(when, {
      is,
      then: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === LANGUAGES.HY ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
    }),
  [`${organizationName}RU`]: Yup.string()
    .nullable()
    .when(when, {
      is,
      then: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === LANGUAGES.RU ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
    }),
  [`${organizationName}EN`]: Yup.string()
    .nullable()
    .when(when, {
      is,
      then: Yup.string().when('formLanguage', (formLanguage, sch) => (formLanguage === LANGUAGES.EN ? sch.required('validationErrors.isRequired').isCorrectLanguage() : sch)),
    }),
  [email]: Yup.string()
    .nullable()
    .when(when, {
      is,
      then: Yup.string()
        .matches(EMAIL_VALIDATION_PATTERN, 'validationErrors.invalidEmail')
        .required('validationErrors.isRequired'),
    }),
  [phone]: Yup.string()
    .nullable()
    .when(when, {
      is,
      then: Yup.string().test(
        'phone',
        'validationErrors.wrongPhoneNumber.onlyDigits',
        (value) => ONLY_DIGITS_REGEX_ALT.test(value),
      ).test(
        'armenianCellLength',
        'validationErrors.wrongPhoneNumber.length',
        (value = '') => value.length === 0 || value.length === 9,
      )
        .test(
          'armenianCellPrefix',
          'validationErrors.wrongPhoneNumber.code',
          (value = '') => value.length === 0
          || armCellPhoneNumberPrefixes.indexOf(value.slice(0, 3)) !== -1 || value.slice(0, 3) === '010',
        )
        .required('validationErrors.isRequired'),
    }),
});
